import { useState } from "react";
import { Check, Cpu, Loader2, Lock } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import {
  switchErrorText,
  useAronWorkModes,
  useSetAronModel,
} from "../api/aronApi";
import { ARON_DEFAULT_MODE, useAronUiStore } from "../store/aronUiStore";
import {
  CountryFlag,
  PROVIDER_META,
  ProviderMark,
  formatRate,
  formatTokens,
  longContextRates,
  providerFor,
  ratesFor,
} from "./AronProviderMark";
import {
  ModeStars,
  modeExamples,
  modeHint,
  modeLabel,
  modeRating,
} from "./aronModeMeta";
import { formatLimits, limitsTitle } from "../utils/attachmentLimits";
import type { AronReasoningMode, AronWorkMode } from "../types";

// Presets come from the backend (services/modes.py → work_modes), so the model /
// effort / cost text is a single source of truth and new models (Haiku, DeepSeek)
// appear in the picker without a frontend change. Each preset = a concrete model +
// thinking/effort config, chosen for a new conversation and switchable inside an
// existing one (POST conversations/{id}/model/ — applies from the next message).
//
// The composer only has room for a chip: the choice itself lives in a modal, where
// each preset can carry its model, effort and cost without squeezing the answer.

/** The vendor's own mark in a bordered tile, in the vendor's own colour. */
function ModeMark({ model, active }: { model: string; active: boolean }) {
  const provider = providerFor(model);
  const meta = provider ? PROVIDER_META[provider] : null;
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center border bg-[var(--color-bg-surface)] transition-colors ${
        active ? "border-[var(--color-primary)]" : "border-[var(--color-border)]"
      }`}
      style={meta ? { color: meta.color } : undefined}
      title={meta?.name}
    >
      {provider ? <ProviderMark provider={provider} size={19} /> : <Cpu size={17} />}
    </span>
  );
}

/** IN / OUT list price per 1M tokens; a discounted model also shows the old rate. */
function ModeRates({ model }: { model: string }) {
  const rates = ratesFor(model);
  if (!rates) {
    return (
      <span className="text-[10px] text-[var(--color-text-muted)]">
        cennik wg dostawcy
      </span>
    );
  }
  const wasPrice =
    rates.wasInput !== undefined && rates.wasOutput !== undefined
      ? `${formatRate(rates.wasInput)} / ${formatRate(rates.wasOutput)}`
      : null;
  const longRates = longContextRates(rates);
  return (
    <span className="flex flex-col items-end gap-0.5 text-right">
      <span className="font-mono text-[11px] tabular-nums text-[var(--color-text-primary)]">
        <span className="text-[9px] tracking-wider text-[var(--color-text-muted)]">IN </span>
        {formatRate(rates.input)}
        <span className="text-[9px] tracking-wider text-[var(--color-text-muted)]"> OUT </span>
        {formatRate(rates.output)}
      </span>
      {wasPrice && (
        <span
          className="font-mono text-[10px] text-[var(--color-text-muted)]"
          title={rates.promoNote}
        >
          było <s>{wasPrice}</s>
        </span>
      )}
      {longRates && (
        <span
          className="font-mono text-[10px] text-amber-700 dark:text-amber-400"
          title={`Powyżej ${formatTokens(longRates.threshold)} tokenów promptu dostawca liczy CAŁE żądanie drożej — nie tylko nadwyżkę.`}
        >
          &gt;{formatTokens(longRates.threshold)}: {formatRate(longRates.input)} / {formatRate(longRates.output)}
        </span>
      )}
    </span>
  );
}

/** Exported so the ARON planner can offer the very same model cards when defining
 *  a scheduled task — one place decides how a model presents itself. */
export function ModeCard({
  mode,
  active,
  onPick,
}: {
  mode: AronWorkMode;
  active: boolean;
  /** Absent → the card is a read-only summary, not a choice. */
  onPick?: () => void;
}) {
  const provider = providerFor(mode.model);
  const meta = provider ? PROVIDER_META[provider] : null;
  const rates = ratesFor(mode.model);
  const promo = rates?.wasInput !== undefined;
  const rating = modeRating(mode);
  const hint = modeHint(mode);
  const examples = modeExamples(mode);

  const Tag = onPick ? "button" : "div";

  return (
    <Tag
      {...(onPick
        ? ({ type: "button", onClick: onPick, "aria-pressed": active } as const)
        : {})}
      className={`group relative flex flex-col overflow-hidden border p-3 pl-4 text-left transition-all duration-100 ${
        active
          ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
          : "border-[var(--color-border)] bg-[var(--color-bg-surface)]"
      } ${
        onPick && !active
          ? "hover:-translate-y-px hover:border-[var(--color-border-strong)] hover:shadow-[3px_3px_0_0_rgba(10,10,10,0.10)]"
          : ""
      }`}
    >
      {/* Rail — the selected preset is readable from the corner of the eye. */}
      <span
        className={`absolute top-0 left-0 h-full w-[3px] ${
          active
            ? "bg-[var(--color-primary)]"
            : "bg-transparent group-hover:bg-[var(--color-border-strong)]"
        }`}
      />

      <span className="flex items-start gap-2.5">
        <ModeMark model={mode.model} active={active} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
              {modeLabel(mode)}
            </span>
            {promo && (
              <span className="border border-[var(--color-primary)] px-1 font-mono text-[9px] font-bold tracking-wider text-[var(--color-primary)]">
                PROMO
              </span>
            )}
            <span className="ml-auto flex items-center gap-1.5 pl-1">
              <span
                className="border border-[var(--color-border)] px-1 font-mono text-[10px] leading-4 text-[var(--color-text-secondary)]"
                title="Poziom kosztu tego trybu"
              >
                {mode.cost}
              </span>
              {active && <Check size={14} className="text-[var(--color-primary)]" />}
            </span>
          </span>
          <span className="mt-0.5 block truncate font-mono text-[11px] text-[var(--color-text-secondary)]">
            {mode.model}
          </span>
          <span className="block truncate text-[11px] text-[var(--color-text-muted)]">
            {mode.detail}
          </span>
          {rating !== null && (
            <span className="mt-1 flex items-center gap-1.5">
              <ModeStars rating={rating} />
              <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                {rating.toFixed(1).replace(".", ",")}
              </span>
            </span>
          )}
        </span>
      </span>

      {hint && (
        <span className="mt-2 block text-[11px] leading-relaxed text-[var(--color-text-muted)]">
          {hint}
        </span>
      )}

      {/* „Do czego" konkretami. Opis parametrów nie rozstrzygał wyboru — trzy zadania
          z własnego podwórka rozstrzygają (patrz aronModeMeta.MODE_META.examples). */}
      {examples.length > 0 && (
        <span className="mt-2 block text-[11px] leading-relaxed text-[var(--color-text-muted)]">
          <span className="font-mono text-[9px] uppercase tracking-wider">
            do czego:
          </span>
          {examples.map((example) => (
            <span key={example} className="mt-0.5 flex gap-1.5">
              <span aria-hidden="true">·</span>
              <span>{example}</span>
            </span>
          ))}
        </span>
      )}

      {/* Limity załączników = limity DOSTAWCY tego trybu; te same liczby egzekwuje
          kompozytor i backend, więc odmowa przy wysyłce nigdy nie jest niespodzianką. */}
      {mode.limits && (
        <span
          className="mt-2 block border-l-2 border-[var(--color-border)] pl-2 text-[11px] leading-relaxed text-[var(--color-text-muted)]"
          title={limitsTitle(mode.limits)}
          data-testid="mode-limits"
        >
          <span className="font-mono text-[9px] tracking-wider uppercase">załączniki: </span>
          {formatLimits(mode.limits)}
        </span>
      )}

      <span className="mt-auto flex items-end justify-between gap-2 border-t border-[var(--color-border-muted)] pt-2.5">
        <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-wider text-[var(--color-text-muted)] uppercase">
          {meta && <CountryFlag country={meta.country} />}
          {meta ? `${meta.name} · ${meta.countryLabel}` : "—"}
        </span>
        <ModeRates model={mode.model} />
      </span>
    </Tag>
  );
}

export function AronModelPicker({
  locked,
  lockedValue,
  conversationId,
  busy,
}: {
  locked?: boolean;
  lockedValue?: string;
  /** Existing conversation → picking writes to THIS thread and applies from the next
   *  message. Absent (e.g. the mail view, which shows a thread it doesn't own the
   *  composer for) → the chip stays a read-only summary. */
  conversationId?: string | null;
  /** A turn is in flight: the backend refuses a switch then (409), so the cards are
   *  shown but not clickable — with the reason, instead of a failing click. */
  busy?: boolean;
} = {}) {
  const mode = useAronUiStore((s) => s.modelMode);
  const setMode = useAronUiStore((s) => s.setModelMode);
  const { data } = useAronWorkModes();
  const setModel = useSetAronModel();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modes = data?.reasoning_modes ?? [];
  if (modes.length === 0) return null;

  const chip =
    "inline-flex h-6 shrink-0 items-center gap-1 border border-gray-200 px-1.5 text-[11px] text-gray-600 transition-colors dark:border-gray-700 dark:text-gray-300";

  // Tryb domyślny idzie na początek — kafel z napisem „Domyślny" na trzeciej
  // pozycji czytałby się jak pomyłka. Reszta zostaje w kolejności backendu
  // (od najtańszego).
  const ordered = [...modes].sort(
    (a, b) =>
      Number(b.value === ARON_DEFAULT_MODE) - Number(a.value === ARON_DEFAULT_MODE),
  );

  /** `frozen` → the cards render as read-only summaries: a disabled-looking button
   *  would still fire a click in some browsers, and a switch refused by the server
   *  (turn in flight) must not even be attempted. */
  const grid = (
    current: string | undefined,
    pick: (m: AronWorkMode) => void,
    frozen = false,
  ) => (
    <div className="grid gap-2 sm:grid-cols-2">
      {ordered.map((m) => (
        <ModeCard
          key={m.value}
          mode={m}
          active={m.value === current}
          onPick={frozen ? undefined : () => pick(m)}
        />
      ))}
    </div>
  );

  // Existing conversation. The model is NOT frozen any more — it can be switched
  // mid-thread (the next turn replays the thread as plain text, so nothing
  // provider-specific crosses the switch). The chip carries the whole identity
  // (mark, flag, tier) and opens the same cards as the new-conversation picker.
  if (locked) {
    const editable = !!conversationId;
    const known = modes.find((x) => x.value === lockedValue);
    // A thread can sit on a mode that is no longer offered (provider flag turned off,
    // preset hidden in the picker). The chip used to vanish then — and with it the
    // only way out of that mode. Show a plain stand-in card instead, so such a thread
    // can still be moved onto something the server actually accepts.
    const m: AronWorkMode | undefined =
      known ??
      (editable && lockedValue
        ? {
            value: lockedValue as AronReasoningMode,
            label: lockedValue,
            model: "",
            cost: "",
            detail: "tryb niedostępny na liście",
            hint: "",
            vision: false,
            addendum: false,
          }
        : undefined);
    if (!m) return null;
    const provider = providerFor(m.model);
    const meta = provider ? PROVIDER_META[provider] : null;
    const rates = ratesFor(m.model);
    const longRates = rates ? longContextRates(rates) : null;
    const priceHint = rates
      ? ` Cena: ${formatRate(rates.input)} / ${formatRate(rates.output)} za 1M tokenów (wejście/wyjście).`
        + (longRates
          ? ` Powyżej ${formatTokens(longRates.threshold)} tokenów promptu: ${formatRate(longRates.input)} / ${formatRate(longRates.output)} za całe żądanie.`
          : "")
      : "";
    const limitsHint = m.limits ? ` Załączniki: ${formatLimits(m.limits)}.` : "";
    const pending = setModel.isPending;
    return (
      <>
        <button
          type="button"
          onClick={() => {
            setError(null);
            setOpen(true);
          }}
          className={`${chip} hover:border-red-400 hover:text-red-600`}
          title={
            editable
              ? `Model tej rozmowy: ${modeLabel(m)} (${m.model} · ${m.detail}).${priceHint}${limitsHint} Kliknij, aby zmienić — zmiana obowiązuje od następnej wiadomości.`
              : `Model tej rozmowy: ${modeLabel(m)} (${m.model} · ${m.detail}).${priceHint}${limitsHint} Kliknij po szczegóły.`
          }
        >
          {pending ? (
            <Loader2 size={11} className="animate-spin text-gray-400" />
          ) : !editable ? (
            <Lock size={11} className="text-gray-400" />
          ) : null}
          {provider && meta ? (
            <span style={{ color: meta.color }}>
              <ProviderMark provider={provider} size={12} />
            </span>
          ) : (
            <Cpu size={11} className="text-gray-400" />
          )}
          <span className="font-medium">{modeLabel(m)}</span>
          <span className="text-gray-400">{m.cost}</span>
          {meta && <CountryFlag country={meta.country} />}
        </button>

        <Modal
          open={open}
          onClose={() => setOpen(false)}
          title="Model tej rozmowy"
          size={editable ? "lg" : "md"}
          closeOnBackdrop
        >
          {!editable ? (
            <>
              <p className="mb-3 border-b border-[var(--color-border)] pb-3 text-xs text-[var(--color-text-secondary)]">
                Model tego wątku. Zmienisz go w oknie rozmowy.
              </p>
              <ModeCard mode={m} active />
            </>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-[var(--color-border)] pb-3">
                <p className="text-xs text-[var(--color-text-secondary)]">
                  Zmiana obowiązuje <b>od następnej wiadomości</b> — dotychczasowa
                  rozmowa zostaje, nowy model dostaje ją jako tekst. Koszt każdej
                  tury liczy się modelowi, który ją wykonał.
                </p>
                <p className="font-mono text-[10px] tracking-wider text-[var(--color-text-muted)] uppercase">
                  ceny dostawcy w USD za 1 mln tokenów · in = wejście / out = wyjście
                  <br />
                  gwiazdki = nasza ocena jakości na trudnych zadaniach (0–5)
                </p>
              </div>
              {busy && (
                <p className="mb-2 border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
                  ARON właśnie odpowiada — model zmienisz po zakończeniu tej
                  odpowiedzi.
                </p>
              )}
              {!known && (
                <p className="mb-2 border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
                  Ta rozmowa działa na trybie, którego nie ma już na liście
                  (wyłączony u dostawcy albo wycofany). Wybierz jeden z dostępnych,
                  żeby móc pisać dalej.
                </p>
              )}
              {known && !m.vision && (
                <p className="mb-2 text-[11px] text-[var(--color-text-muted)]">
                  Obecny model nie czyta obrazów. Po przełączeniu na model z
                  obsługą załączników spinacz wróci — wcześniejsze załączniki
                  nowy model dostanie od następnej wiadomości.
                </p>
              )}
              {error && (
                <p className="mb-2 border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                  {error}
                </p>
              )}
              {grid(
                m.value,
                (picked) => {
                  if (picked.value === m.value) {
                    setOpen(false);
                    return;
                  }
                  setError(null);
                  setModel.mutate(
                    {
                      conversationId: conversationId!,
                      mode: picked.value as AronReasoningMode,
                    },
                    {
                      onSuccess: () => setOpen(false),
                      onError: (err) =>
                        setError(
                          switchErrorText(
                            err,
                            "Nie udało się zmienić modelu. Spróbuj ponownie.",
                          ),
                        ),
                    },
                  );
                },
                busy || pending,
              )}
            </>
          )}
        </Modal>
      </>
    );
  }

  const active = modes.find((m) => m.value === mode) ?? modes[0];
  const activeProvider = providerFor(active.model);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={`Tryb pracy nowej rozmowy: ${modeLabel(active)} (${active.model} · ${active.detail}). Kliknij, aby zmienić.`}
        className={`${chip} hover:border-red-400 hover:text-red-600`}
      >
        {activeProvider ? (
          <span style={{ color: PROVIDER_META[activeProvider].color }}>
            <ProviderMark provider={activeProvider} size={12} />
          </span>
        ) : (
          <Cpu size={11} className="text-red-500" />
        )}
        <span className="font-medium">{modeLabel(active)}</span>
        <span className="text-gray-400">{active.cost}</span>
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Tryb pracy ARONa"
        size="lg"
        closeOnBackdrop
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-[var(--color-border)] pb-3">
          <p className="text-xs text-[var(--color-text-secondary)]">
            Wybór dotyczy <b>nowej</b> rozmowy — w trwającej rozmowie model
            zmienisz tym samym przyciskiem, ze skutkiem od następnej wiadomości.
          </p>
          <p className="font-mono text-[10px] tracking-wider text-[var(--color-text-muted)] uppercase">
            ceny dostawcy w USD za 1 mln tokenów · in = wejście / out = wyjście
            <br />
            gwiazdki = nasza ocena jakości na trudnych zadaniach (0–5)
          </p>
        </div>
        {grid(mode, (m) => {
          setMode(m.value);
          setOpen(false);
        })}
      </Modal>
    </>
  );
}
